/** Customer volume trend: customers with the biggest booking-volume swings. */
import type { PatternPayload, VolumeTrendEntry } from "@/types/feed";

import { customerBookingsHref } from "../../feedLinks";
import { compareScale, formatDeltaPct } from "../formatInsight";
import { explainerFor, periodLabel } from "../insightCopy";
import { ChangeRow, EntitySection, InsightShell, PeriodBanner } from "../primitives";

function VolumeSection({ title, entries }: { title: string; entries: VolumeTrendEntry[] }) {
  const max = compareScale(entries.flatMap((e) => [e.prior_teu, e.current_teu]));
  return (
    <EntitySection title={title}>
      {entries.map((e) => {
        const delta = formatDeltaPct(e.pct_change);
        return (
          <ChangeRow
            key={e.consignee}
            name={e.consignee}
            href={customerBookingsHref(e.consignee)}
            party
            primary={delta.text}
            primaryClassName={delta.className}
            deltaPct={e.pct_change}
            bar={{
              type: "compare",
              prior: e.prior_teu,
              current: e.current_teu,
              unit: "TEU",
              max,
            }}
          />
        );
      })}
    </EntitySection>
  );
}

export function VolumeTrendView({ payload }: { payload: PatternPayload }) {
  const period = periodLabel(payload as unknown as Record<string, unknown>);
  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {period && <PeriodBanner label={period} />}
      {payload.top_declines && payload.top_declines.length > 0 && (
        <VolumeSection title="Biggest drops" entries={payload.top_declines} />
      )}
      {payload.top_increases && payload.top_increases.length > 0 && (
        <VolumeSection title="Biggest gains" entries={payload.top_increases} />
      )}
    </InsightShell>
  );
}
